import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {Edit, MyLocation, PanTool, Polyline, RadioButtonUnchecked} from '@mui/icons-material';
import dayjs from 'dayjs';
import CheckInPhoto from '../common/CheckInPhoto';
import {useClient} from '../core/client';
import MapDialog from '../core/components/MapDialog';
import DefendantDialog from './DefendantDialog';
import CheckinScheduleEditor from '../checkin/CheckinScheduleEditor';

const GOOGLE_MAPS_API_KEY = 'AIzaSyCgeNF-haEfOwJ_NT7nFLVpAL_YrsEiTEg';

const InfoPair = ({ label, value }) => (
    <Grid container alignItems="center">
        <Grid item xs={12} sm={4} md={3}>
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
        </Grid>
        <Grid item xs={12} sm={8} md={9}>
            <Typography variant="body1">{value || '—'}</Typography>
        </Grid>
    </Grid>
);

const getCoords = (location) => {
    if (!location) return null;
    if (location.coords) {
        return {
            lat: location.coords.latitude,
            lng: location.coords.longitude,
            accuracy: location.coords.accuracy,
            timestamp: location.timestamp,
        };
    }
    if (location.latitude && location.longitude) {
        return {
            lat: location.latitude,
            lng: location.longitude,
            accuracy: location.accuracy,
            timestamp: location.timestamp,
        };
    }
    return null;
};

const getLocationTimestamp = (locationEntry) => {
    if (!locationEntry) return 0;
    if (locationEntry.date) {
        const parsed = new Date(locationEntry.date).getTime();
        if (!Number.isNaN(parsed)) return parsed;
    }
    const embeddedTimestamp = locationEntry.location?.timestamp ?? locationEntry.timestamp;
    if (embeddedTimestamp) {
        const numeric = Number(embeddedTimestamp);
        return Number.isNaN(numeric) ? 0 : numeric;
    }
    return 0;
};

const pickLatestLocation = (locations = []) =>
    locations.reduce((latest, current) => {
        if (!latest) return current;
        return getLocationTimestamp(current) > getLocationTimestamp(latest) ? current : latest;
    }, null);

const formatDob = (dob) => {
    if (!dob) return '—';
    const parsed = dayjs(dob);
    return parsed.isValid() ? parsed.format('MMM D, YYYY') : dob;
};

const formatPhoneNumber = (phone) => {
    if (!phone) return '—';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 7) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    return phone;
};

const DefendantProfile = () => {
    const { id } = useParams();
    const client = useClient();
    const [defendant, setDefendant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mapOpen, setMapOpen] = useState(false);
    const [mapLocation, setMapLocation] = useState(null);
    const [requestingLocation, setRequestingLocation] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [scheduleEditorOpen, setScheduleEditorOpen] = useState(false);
    const [geofenceOpen, setGeofenceOpen] = useState(false);
    const [geofenceEvents, setGeofenceEvents] = useState([]);
    const [latestPingLocation, setLatestPingLocation] = useState(null);
    const [geofenceSubmitting, setGeofenceSubmitting] = useState(false);
    const [geofenceError, setGeofenceError] = useState('');
    const [geofenceAdvanced, setGeofenceAdvanced] = useState(false);
    const [geofenceGeocoding, setGeofenceGeocoding] = useState(false);
    const [geofenceDialogReady, setGeofenceDialogReady] = useState(false);
    const [geofenceForm, setGeofenceForm] = useState({
        address: '',
        latitude: '',
        longitude: '',
        radiusMiles: '0.15',
        shapeType: 'circle',
        polygon: [],
    });
    const [geofenceMapElement, setGeofenceMapElement] = useState(null);
    const [geofenceLoading, setGeofenceLoading] = useState(false);
    const [geofenceExisting, setGeofenceExisting] = useState(null);
    const geofenceMapInstance = useRef(null);
    const geofenceCircleRef = useRef(null);
    const geofencePolygonRef = useRef(null);
    const geofenceDrawingRef = useRef(null);
    const [geofenceDrawMode, setGeofenceDrawMode] = useState('circle');
    const [geofenceDrawingReady, setGeofenceDrawingReady] = useState(false);
    const [checkinDialogOpen, setCheckinDialogOpen] = useState(false);
    const [selectedCheckin, setSelectedCheckin] = useState(null);
    const pingTimeoutRef = useRef(null);
    const pingPollRef = useRef(null);

    const fetchDefendant = useCallback(async () => {
        try {
            const { data } = await client.get(`/defendants/${id}`);
            setDefendant(data);
            try {
                const eventsResponse = await client.get(`/geofence-events/defendant/${id}`);
                if (Array.isArray(eventsResponse.data)) {
                    setGeofenceEvents(eventsResponse.data);
                }
            } catch (eventError) {
                console.error('Error fetching geofence events:', eventError);
            }
        } catch (error) {
            console.error('Error fetching defendant:', error);
        }
    }, [client, id]);

    useEffect(() => {
        let isActive = true;
        const load = async () => {
            setLoading(true);
            try {
                await fetchDefendant();
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            isActive = false;
        };
    }, [fetchDefendant]);

    useEffect(() => {
        return () => {
            if (pingTimeoutRef.current) {
                clearTimeout(pingTimeoutRef.current);
            }
            if (pingPollRef.current) {
                clearTimeout(pingPollRef.current);
            }
        };
    }, []);

    const schedules = useMemo(() => {
        const raw = defendant?.checkinSchedules;
        if (!raw) return [];
        return Array.isArray(raw) ? raw : [raw];
    }, [defendant?.checkinSchedules]);

    const schedule = useMemo(() => {
        if (schedules.length === 0) return null;
        const active = schedules.find((item) => item?.status === 'ACTIVE');
        if (active) return active;
        return [...schedules].sort(
            (a, b) => new Date(b?.startDate || 0).getTime() - new Date(a?.startDate || 0).getTime()
        )[0];
    }, [schedules]);

    const defendantStatusLabel = defendant?.status === 'INACTIVE' ? 'Inactive' : 'Active';
    const checkins = useMemo(() => {
        const allCheckins = schedules.flatMap((item) =>
            Array.isArray(item?.checkins) ? item.checkins : []
        );
        if (!allCheckins.length) return [];
        return [...allCheckins].sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
    }, [schedules]);

    const lastCheckin = checkins[0];
    const lastLocationCheckin = checkins.find((c) => c.location);
    const latestLocationFromDefendant = useMemo(
        () => pickLatestLocation(defendant?.locations || []),
        [defendant]
    );
    const latestLocationRecord = useMemo(
        () => pickLatestLocation([latestLocationFromDefendant, latestPingLocation].filter(Boolean)),
        [latestLocationFromDefendant, latestPingLocation]
    );
    const lastKnownCoords =
        getCoords(lastLocationCheckin?.location) ||
        getCoords(latestLocationRecord?.location || latestLocationRecord);
    const lastKnownTimestamp =
        lastLocationCheckin?.date ||
        latestLocationRecord?.location?.timestamp ||
        latestLocationRecord?.date;

    const defendantAddress = useMemo(() => {
        if (!defendant) return '';
        return [defendant.address, defendant.city, defendant.state, defendant.zip]
            .filter(Boolean)
            .join(', ');
    }, [defendant]);

    const activityItems = useMemo(() => {
        const checkinItems = checkins.map((checkin) => ({
            type: 'checkin',
            id: checkin.id || checkin.date,
            date: checkin.date,
            status: checkin.status || 'Pending',
            location: checkin.location,
            raw: checkin,
        }));
        const locationItems = (defendant?.locations || []).map((entry) => ({
            type: 'location',
            id: entry.id || entry._id || entry.date || entry.location?.timestamp,
            date: entry.date || entry.location?.timestamp,
            status: 'Location Ping',
            location: entry.location || entry,
            raw: entry,
        }));
        const geofenceItems = (geofenceEvents || []).map((event) => ({
            type: 'geofence',
            id: event._id || event.id || `${event.geofenceId}-${event.timestamp}`,
            date: event.timestamp || event.createdAt,
            status: event.eventType === 'exit' ? 'Geofence Exit' : 'Geofence Enter',
            location:
                Number.isFinite(Number(event.latitude)) && Number.isFinite(Number(event.longitude))
                    ? {
                          latitude: Number(event.latitude),
                          longitude: Number(event.longitude),
                          accuracy: event.accuracy,
                          timestamp: event.timestamp || event.createdAt,
                      }
                    : null,
            raw: event,
        }));
        return [...checkinItems, ...locationItems]
            .filter((item) => item.date)
            .concat(geofenceItems)
            .filter((item) => item.date)
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [checkins, defendant, geofenceEvents]);

    const handleGeofenceMapRef = useCallback((node) => {
        if (node && node instanceof Element) {
            setGeofenceMapElement(node);
        } else {
            setGeofenceMapElement(null);
        }
    }, []);

    const clearGeofenceShapes = useCallback(() => {
        if (geofenceCircleRef.current) {
            geofenceCircleRef.current.setMap(null);
            geofenceCircleRef.current = null;
        }
        if (geofencePolygonRef.current) {
            geofencePolygonRef.current.setMap(null);
            geofencePolygonRef.current = null;
        }
    }, []);

    const teardownGeofenceMap = useCallback(() => {
        clearGeofenceShapes();
        if (geofenceDrawingRef.current) {
            geofenceDrawingRef.current.setMap(null);
            geofenceDrawingRef.current = null;
        }
        if (geofenceMapInstance.current) {
            if (window.google?.maps?.event) {
                window.google.maps.event.clearInstanceListeners(geofenceMapInstance.current);
            }
            geofenceMapInstance.current = null;
        }
        setGeofenceDrawingReady(false);
    }, [clearGeofenceShapes]);

    const milesToMeters = useCallback((miles) => miles * 1609.344, []);
    const metersToMiles = useCallback((meters) => meters / 1609.344, []);

    const readPolygonPath = useCallback((polygon) => {
        const path = polygon.getPath();
        const points = [];
        for (let i = 0; i < path.getLength(); i += 1) {
            const point = path.getAt(i);
            points.push({
                latitude: point.lat(),
                longitude: point.lng(),
            });
        }
        return points;
    }, []);

    const normalizeGeofence = useCallback((payload) => {
        if (!payload) return null;
        if (Array.isArray(payload)) return payload[0] || null;
        if (Array.isArray(payload.data)) return payload.data[0] || null;
        if (payload.geofence) return payload.geofence;
        return payload;
    }, []);

    useEffect(() => {
        if (geofenceForm.shapeType === 'polygon' && geofenceCircleRef.current) {
            geofenceCircleRef.current.setMap(null);
            geofenceCircleRef.current = null;
        }
        if (geofenceForm.shapeType === 'circle' && geofencePolygonRef.current) {
            geofencePolygonRef.current.setMap(null);
            geofencePolygonRef.current = null;
        }
    }, [geofenceForm.shapeType]);

    useEffect(() => {
        if (geofenceDrawMode === 'pan') {
            return;
        }
        clearGeofenceShapes();
        setGeofenceForm((prev) => ({
            ...prev,
            shapeType: geofenceDrawMode,
            ...(geofenceDrawMode === 'circle' ? { polygon: [] } : {}),
        }));
    }, [clearGeofenceShapes, geofenceDrawMode]);

    useEffect(() => {
        if (!geofenceOpen || !geofenceAdvanced || !geofenceDialogReady) return;
        if (!geofenceMapElement) return;
        let isActive = true;

        const ensureGoogleMaps = () =>
            new Promise((resolve, reject) => {
                if (window.google && window.google.maps) {
                    resolve();
                    return;
                }
                const existing = document.getElementById('google-maps-script');
                if (existing) {
                    existing.addEventListener('load', resolve);
                    existing.addEventListener('error', reject);
                    return;
                }
                const script = document.createElement('script');
                script.id = 'google-maps-script';
                script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=drawing&callback=initGeofenceMap`;
                script.async = true;
                script.defer = true;
                window.initGeofenceMap = () => resolve();
                script.onerror = reject;
                document.body.appendChild(script);
            });

        const waitForMapContainer = () =>
            new Promise((resolve, reject) => {
                let attempts = 0;
                const tick = () => {
                    if (!geofenceMapElement) {
                        reject(new Error('Map container missing'));
                        return;
                    }
                    const rect = geofenceMapElement.getBoundingClientRect();
                    if (rect.width > 0 && rect.height > 0) {
                        resolve();
                        return;
                    }
                    attempts += 1;
                    if (attempts > 30) {
                        reject(new Error('Map container has no size'));
                        return;
                    }
                    requestAnimationFrame(tick);
                };
                tick();
            });

        const updateCircle = () => {
            if (!geofenceMapInstance.current || !window.google) return;
            const lat = Number.parseFloat(geofenceForm.latitude);
            const lng = Number.parseFloat(geofenceForm.longitude);
            const radiusMiles = Number.parseFloat(geofenceForm.radiusMiles) || 0.15;
            const radius = milesToMeters(radiusMiles);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

            const center = { lat, lng };
            geofenceMapInstance.current.setCenter(center);
            if (!geofenceCircleRef.current) {
                geofenceCircleRef.current = new window.google.maps.Circle({
                    map: geofenceMapInstance.current,
                    center,
                    radius,
                    strokeColor: '#1EB980',
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    fillColor: '#1EB980',
                    fillOpacity: 0.2,
                    editable: true,
                });
            } else {
                geofenceCircleRef.current.setCenter(center);
                geofenceCircleRef.current.setRadius(radius);
            }
            window.google.maps.event.trigger(geofenceMapInstance.current, 'resize');
        };

        const updatePolygon = () => {
            if (!geofenceMapInstance.current || !window.google) return;
            if (!geofenceForm.polygon?.length) return;
            const path = geofenceForm.polygon
                .map((point) => {
                    const lat = Number.parseFloat(point.latitude ?? point.lat);
                    const lng = Number.parseFloat(point.longitude ?? point.lng);
                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                    return { lat, lng };
                })
                .filter(Boolean);
            if (path.length < 3) return;

            if (!geofencePolygonRef.current) {
                geofencePolygonRef.current = new window.google.maps.Polygon({
                    map: geofenceMapInstance.current,
                    paths: path,
                    strokeColor: '#1EB980',
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    fillColor: '#1EB980',
                    fillOpacity: 0.2,
                    editable: true,
                });
                const polygonPath = geofencePolygonRef.current.getPath();
                polygonPath.addListener('insert_at', () => {
                    setGeofenceForm((prev) => ({
                        ...prev,
                        polygon: readPolygonPath(geofencePolygonRef.current),
                    }));
                });
                polygonPath.addListener('set_at', () => {
                    setGeofenceForm((prev) => ({
                        ...prev,
                        polygon: readPolygonPath(geofencePolygonRef.current),
                    }));
                });
                polygonPath.addListener('remove_at', () => {
                    setGeofenceForm((prev) => ({
                        ...prev,
                        polygon: readPolygonPath(geofencePolygonRef.current),
                    }));
                });
            } else {
                geofencePolygonRef.current.setPath(path);
            }

            const bounds = new window.google.maps.LatLngBounds();
            path.forEach((coord) => bounds.extend(coord));
            geofenceMapInstance.current.fitBounds(bounds);
        };

        const geocodeWithMaps = () =>
            new Promise((resolve, reject) => {
                if (!window.google?.maps) {
                    reject(new Error('Google Maps not loaded'));
                    return;
                }
                if (!geofenceForm.address.trim()) {
                    resolve(null);
                    return;
                }
                setGeofenceGeocoding(true);
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ address: geofenceForm.address.trim() }, (results, status) => {
                    setGeofenceGeocoding(false);
                    if (status !== 'OK' || !results?.length) {
                        reject(new Error(`Geocode failed: ${status}`));
                        return;
                    }
                    const location = results[0].geometry?.location;
                    if (!location) {
                        reject(new Error('Geocode returned no location'));
                        return;
                    }
                    resolve({
                        latitude: location.lat().toFixed(6),
                        longitude: location.lng().toFixed(6),
                    });
                });
            });

        ensureGoogleMaps()
            .then(waitForMapContainer)
            .then(async () => {
                if (!isActive || !geofenceMapElement) return;
                const buildDrawingManager = () =>
                    new window.google.maps.drawing.DrawingManager({
                        drawingMode: null,
                        drawingControl: false,
                        drawingControlOptions: {
                            drawingModes: [
                                window.google.maps.drawing.OverlayType.CIRCLE,
                                window.google.maps.drawing.OverlayType.POLYGON,
                            ],
                        },
                        circleOptions: {
                            strokeColor: '#1EB980',
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                            fillColor: '#1EB980',
                            fillOpacity: 0.2,
                            editable: true,
                        },
                        polygonOptions: {
                            strokeColor: '#1EB980',
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                            fillColor: '#1EB980',
                            fillOpacity: 0.2,
                            editable: true,
                        },
                    });

                if (!geofenceMapInstance.current) {
                    const lat = Number.parseFloat(geofenceForm.latitude) || 39.809734;
                    const lng = Number.parseFloat(geofenceForm.longitude) || -98.55562;
                    geofenceMapInstance.current = new window.google.maps.Map(
                        geofenceMapElement,
                        {
                            center: { lat, lng },
                            zoom: geofenceForm.latitude && geofenceForm.longitude ? 14 : 13,
                            disableDefaultUI: true,
                            zoomControl: true,
                        },
                    );
                }

                if (!geofenceDrawingRef.current) {
                    geofenceDrawingRef.current = buildDrawingManager();
                    geofenceDrawingRef.current.setMap(geofenceMapInstance.current);
                    setGeofenceDrawingReady(true);
                }

                if (geofenceDrawingRef.current && !geofenceDrawingRef.current.__ltOverlayBound) {
                    geofenceDrawingRef.current.__ltOverlayBound = true;
                    window.google.maps.event.addListener(
                        geofenceDrawingRef.current,
                        'overlaycomplete',
                        (event) => {
                            if (!geofenceDrawingRef.current) {
                                return;
                            }
                            clearGeofenceShapes();
                            if (event.type === window.google.maps.drawing.OverlayType.CIRCLE) {
                                const circle = event.overlay;
                                geofenceCircleRef.current = circle;
                                geofenceDrawingRef.current.setDrawingMode(null);
                                geofenceDrawingRef.current.setOptions({ drawingMode: null });

                                const center = circle.getCenter();
                                const radiusMeters = circle.getRadius();
                                setGeofenceForm((prev) => ({
                                    ...prev,
                                    shapeType: 'circle',
                                    polygon: [],
                                    latitude: center.lat().toFixed(6),
                                    longitude: center.lng().toFixed(6),
                                    radiusMiles: metersToMiles(radiusMeters).toFixed(2),
                                }));

                                circle.addListener('radius_changed', () => {
                                    const updated = circle.getRadius();
                                    setGeofenceForm((prev) => ({
                                        ...prev,
                                        radiusMiles: metersToMiles(updated).toFixed(2),
                                    }));
                                });

                                circle.addListener('center_changed', () => {
                                    const updatedCenter = circle.getCenter();
                                    setGeofenceForm((prev) => ({
                                        ...prev,
                                        latitude: updatedCenter.lat().toFixed(6),
                                        longitude: updatedCenter.lng().toFixed(6),
                                    }));
                                });
                                return;
                            }

                            if (event.type === window.google.maps.drawing.OverlayType.POLYGON) {
                                const polygon = event.overlay;
                                geofencePolygonRef.current = polygon;
                                geofenceDrawingRef.current.setDrawingMode(null);
                                geofenceDrawingRef.current.setOptions({ drawingMode: null });
                                const points = readPolygonPath(polygon);
                                setGeofenceForm((prev) => ({
                                    ...prev,
                                    shapeType: 'polygon',
                                    polygon: points,
                                }));

                                const path = polygon.getPath();
                                path.addListener('insert_at', () => {
                                    setGeofenceForm((prev) => ({
                                        ...prev,
                                        polygon: readPolygonPath(polygon),
                                    }));
                                });
                                path.addListener('set_at', () => {
                                    setGeofenceForm((prev) => ({
                                        ...prev,
                                        polygon: readPolygonPath(polygon),
                                    }));
                                });
                                path.addListener('remove_at', () => {
                                    setGeofenceForm((prev) => ({
                                        ...prev,
                                        polygon: readPolygonPath(polygon),
                                    }));
                                });
                            }
                        },
                    );
                }

                if (
                    geofenceForm.address.trim() &&
                    !Number.isFinite(Number.parseFloat(geofenceForm.latitude)) &&
                    !Number.isFinite(Number.parseFloat(geofenceForm.longitude))
                ) {
                    try {
                        const location = await geocodeWithMaps();
                        if (location && isActive) {
                            setGeofenceForm((prev) => ({ ...prev, ...location }));
                        }
                    } catch (error) {
                        console.error('Failed to geocode address for map', error);
                    }
                }

                if (geofenceForm.shapeType === 'circle' && geofenceDrawMode === 'circle') {
                    updateCircle();
                }
                if (geofenceForm.shapeType === 'polygon' && geofenceDrawMode === 'polygon') {
                    updatePolygon();
                }
                setTimeout(() => {
                    if (geofenceMapInstance.current && window.google?.maps?.event) {
                        window.google.maps.event.trigger(geofenceMapInstance.current, 'resize');
                    }
                }, 0);
            })
            .catch((error) => {
                console.error('Failed to load Google Maps', error);
                if (isActive) {
                    setGeofenceError('Map failed to load. Please try again.');
                }
            });

        return () => {
            isActive = false;
            geofenceDrawingRef.current = null;
            setGeofenceDrawingReady(false);
        };
    }, [
        geofenceAdvanced,
        clearGeofenceShapes,
        geofenceDrawMode,
        geofenceForm.latitude,
        geofenceForm.longitude,
        geofenceForm.radiusMiles,
        geofenceForm.shapeType,
        geofenceForm.polygon,
        geofenceForm.address,
        geofenceOpen,
        geofenceDialogReady,
        geofenceMapElement,
        milesToMeters,
        metersToMiles,
        readPolygonPath,
    ]);


    useEffect(() => {
        if (!geofenceDrawingReady || !geofenceDrawingRef.current || !window.google?.maps?.drawing) return;
        if (!geofenceAdvanced || geofenceDrawMode === 'pan') {
            geofenceDrawingRef.current.setDrawingMode(null);
            geofenceDrawingRef.current.setOptions({ drawingMode: null });
            return;
        }
        const mode =
            geofenceDrawMode === 'polygon'
                ? window.google.maps.drawing.OverlayType.POLYGON
                : window.google.maps.drawing.OverlayType.CIRCLE;
        geofenceDrawingRef.current.setDrawingMode(mode);
        geofenceDrawingRef.current.setOptions({
            drawingMode: mode,
            drawingControlOptions: {
                drawingModes: [
                    window.google.maps.drawing.OverlayType.CIRCLE,
                    window.google.maps.drawing.OverlayType.POLYGON,
                ],
            },
        });
    }, [geofenceAdvanced, geofenceDrawMode, geofenceDrawingReady]);

    const openMapForLocation = (location) => {
        if (!location) return;
        setMapLocation(location);
        setMapOpen(true);
    };

    const openCheckinDialog = (checkin) => {
        setSelectedCheckin(checkin || null);
        setCheckinDialogOpen(true);
    };

    const closeCheckinDialog = () => {
        setCheckinDialogOpen(false);
        setSelectedCheckin(null);
    };

    const handleRequestLocation = async () => {
        if (!defendant) return;
        setRequestingLocation(true);
        try {
            const baselineTimestamp = getLocationTimestamp(
                pickLatestLocation(defendant?.locations || [])
            );
            const { data } = await client.get(`/defendants/location/${defendant.id}`);
            if (Array.isArray(data)) {
                const latest = pickLatestLocation(data);
                if (latest) {
                    setLatestPingLocation(latest);
                }
            }
            if (pingTimeoutRef.current) {
                clearTimeout(pingTimeoutRef.current);
            }
            const poll = async (attempt = 0) => {
                if (attempt >= 6) return;
                try {
                    const response = await client.get(`/defendants/${defendant.id}`);
                    const latest = pickLatestLocation(response.data?.locations || []);
                    const latestTimestamp = getLocationTimestamp(latest);
                    if (latest && latestTimestamp > baselineTimestamp) {
                        setLatestPingLocation(latest);
                        setDefendant(response.data);
                        return;
                    }
                } catch (error) {
                    console.error('Failed to refresh defendant location', error);
                }
                pingPollRef.current = setTimeout(() => poll(attempt + 1), 5000);
            };
            pingPollRef.current = setTimeout(() => poll(0), 5000);
            pingTimeoutRef.current = setTimeout(() => {
                fetchDefendant();
            }, 20000);
        } catch (error) {
            console.error('Failed to request location', error);
        } finally {
            setRequestingLocation(false);
        }
    };

    const loadGeofence = useCallback(async () => {
        if (!defendant) return;
        setGeofenceLoading(true);
        try {
            const response = await client.get(`/geofences/defendant/${defendant.id}`);
            const geofence = normalizeGeofence(response.data);
            if (!geofence) {
                setGeofenceExisting(null);
                return;
            }
            const nextShapeType =
                geofence.shapeType && geofence.shapeType.toLowerCase() === 'polygon'
                    ? 'polygon'
                    : 'circle';
            const radiusMiles =
                geofence.radiusMiles ??
                (Number.isFinite(Number.parseFloat(geofence.radius))
                    ? metersToMiles(Number.parseFloat(geofence.radius)).toFixed(2)
                    : null);
            setGeofenceExisting(geofence);
            setGeofenceForm((prev) => ({
                ...prev,
                address: geofence.address ?? prev.address,
                latitude: geofence.latitude?.toString?.() ?? geofence.latitude ?? '',
                longitude: geofence.longitude?.toString?.() ?? geofence.longitude ?? '',
                radiusMiles: radiusMiles ?? prev.radiusMiles,
                shapeType: nextShapeType,
                polygon: geofence.polygon ?? [],
            }));
            setGeofenceDrawMode(nextShapeType);
            setGeofenceAdvanced(true);
        } catch (error) {
            if (error?.response?.status === 404) {
                setGeofenceExisting(null);
            } else {
                console.error('Failed to load geofence', error);
            }
        } finally {
            setGeofenceLoading(false);
        }
    }, [client, defendant, metersToMiles, normalizeGeofence]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!defendant) {
        return <Typography variant="h6">Defendant not found</Typography>;
    }

    const checkinPhoto = checkins.find((c) => c.photoUrl)?.photoUrl || defendant.profilePhotoUrl;

    const mapSrc = lastKnownCoords
        ? `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${lastKnownCoords.lat},${lastKnownCoords.lng}&zoom=15`
        : null;

    const handleDefendantSaved = (updated) => {
        if (updated) {
            setDefendant(updated);
        } else {
            fetchDefendant();
        }
        setEditOpen(false);
    };

    const handleScheduleGenerated = async () => {
        await fetchDefendant();
        setScheduleEditorOpen(false);
    };


    const resetGeofenceForm = () => {
        setGeofenceForm({
            address: '',
            latitude: '',
            longitude: '',
            radiusMiles: '0.15',
            shapeType: 'circle',
            polygon: [],
        });
        setGeofenceDrawMode('circle');
        setGeofenceExisting(null);
        setGeofenceError('');
        setGeofenceAdvanced(false);
    };


    const handleOpenGeofence = () => {
        resetGeofenceForm();
        setGeofenceOpen(true);
        if (defendantAddress) {
            setGeofenceForm((prev) => ({ ...prev, address: defendantAddress }));
        }
        loadGeofence();
    };

    const handleCloseGeofence = () => {
        teardownGeofenceMap();
        setGeofenceOpen(false);
    };

    const handleGeofenceChange = (event) => {
        const { name, value } = event.target;
        setGeofenceForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleGeofenceDrawModeChange = (_event, nextMode) => {
        if (!nextMode) return;
        setGeofenceDrawMode(nextMode);
        if (nextMode !== 'pan') {
            clearGeofenceShapes();
            setGeofenceForm((prev) => ({
                ...prev,
                shapeType: nextMode,
                ...(nextMode === 'circle' ? { polygon: [] } : {}),
            }));
        }
        if (!geofenceDrawingRef.current || !window.google?.maps?.drawing) return;
        if (nextMode === 'pan') {
            geofenceDrawingRef.current.setDrawingMode(null);
            return;
        }
        const mode =
            nextMode === 'polygon'
                ? window.google.maps.drawing.OverlayType.POLYGON
                : window.google.maps.drawing.OverlayType.CIRCLE;
        geofenceDrawingRef.current.setDrawingMode(mode);
        geofenceDrawingRef.current.setOptions({
            drawingMode: mode,
            drawingControlOptions: {
                drawingModes: [
                    window.google.maps.drawing.OverlayType.CIRCLE,
                    window.google.maps.drawing.OverlayType.POLYGON,
                ],
            },
        });
    };

    const handleSaveGeofence = async () => {
        if (!defendant) return;
        if (!geofenceForm.address.trim()) {
            setGeofenceError('Please provide an address.');
            return;
        }

        const latitude = Number.parseFloat(geofenceForm.latitude);
        const longitude = Number.parseFloat(geofenceForm.longitude);
        const radiusMiles = Number.parseFloat(geofenceForm.radiusMiles);
        const radius = radiusMiles * 1609.344;

        if (geofenceForm.shapeType === 'circle' && (!Number.isFinite(radiusMiles) || radiusMiles <= 0)) {
            setGeofenceError('Please provide a radius greater than 0 miles.');
            return;
        }
        if (geofenceForm.shapeType === 'polygon' && geofenceForm.polygon.length < 3) {
            setGeofenceError('Please draw a polygon with at least 3 points.');
            return;
        }

        setGeofenceSubmitting(true);
        setGeofenceError('');
        try {
            const payload = {
                defendantId: defendant.id,
                address: geofenceForm.address.trim(),
                active: true,
            };
            if (geofenceForm.shapeType === 'circle') {
                payload.shapeType = 'circle';
                payload.radius = radius;
                if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
                    payload.latitude = latitude;
                    payload.longitude = longitude;
                }
            } else if (geofenceForm.shapeType === 'polygon') {
                payload.shapeType = 'polygon';
                payload.polygon = geofenceForm.polygon;
            }
            if (geofenceExisting?.id) {
                await client.delete(`/geofences/${geofenceExisting.id}`);
            }
            await client.post('/geofences', {
                ...payload,
            });
            teardownGeofenceMap();
            setGeofenceOpen(false);
        } catch (error) {
            console.error('Failed to save geofence', error);
            setGeofenceError('Unable to save this geofence right now. Please try again.');
        } finally {
            setGeofenceSubmitting(false);
        }
    };

    const geofenceActionLabel = geofenceExisting ? 'Edit Geofence' : 'Add Geofence';


    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Grid container spacing={3}>
                <Grid item xs={12} lg={8}>
                    <Stack spacing={3}>
                        <Card>
                            <CardHeader
                                sx={{ alignItems: 'stretch' }}
                                avatar={
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: { xs: 'flex-start', sm: 'center' },
                                            mr: { xs: 1, sm: 2 },
                                            mt: { xs: 0.5, sm: 0 },
                                        }}
                                    >
                                        <CheckInPhoto
                                            alt={`${defendant.first} ${defendant.last}`}
                                            src={checkinPhoto}
                                            sx={{ width: { xs: 80, sm: 110 }, height: { xs: 80, sm: '100%' }, borderRadius: 2 }}
                                        />
                                    </Box>
                                }
                                title={
                                    <Box sx={{ pr: { xs: 2, sm: 0 }, minWidth: 0 }}>
                                        <Typography variant="h5">
                                            {defendant.first && defendant.last ? `${defendant.first} ${defendant.last}` : 'Defendant'}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                        >
                                            {defendant.email}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                            <Chip
                                                size="small"
                                                label={defendantStatusLabel}
                                                color={defendant?.status === 'INACTIVE' ? 'default' : 'success'}
                                                variant="outlined"
                                            />
                                            {schedule?.frequency && (
                                                <Chip size="small" label={`${schedule.frequency} Schedule`} />
                                            )}
                                        </Box>
                                        <Box sx={{ mt: 2, display: { xs: 'block', sm: 'none' } }}>
                                            <Stack spacing={1}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<Edit />}
                                                    sx={{ color: 'common.white', width: '100%' }}
                                                    onClick={() => setEditOpen(true)}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<MyLocation />}
                                                    onClick={handleRequestLocation}
                                                    disabled={requestingLocation}
                                                    sx={{ color: 'common.white', width: '100%' }}
                                                >
                                                    {requestingLocation ? 'Requesting...' : 'Ping Location'}
                                                </Button>
                                            </Stack>
                                        </Box>
                                    </Box>
                                }
                                action={
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{ display: { xs: 'none', sm: 'flex' } }}
                                    >
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<Edit />}
                                            sx={{ color: 'common.white' }}
                                            onClick={() => setEditOpen(true)}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<MyLocation />}
                                            onClick={handleRequestLocation}
                                            disabled={requestingLocation}
                                            sx={{ color: 'common.white' }}
                                        >
                                            {requestingLocation ? 'Requesting...' : 'Ping Location'}
                                        </Button>
                                    </Stack>
                                }
                            />
                            <CardContent>
                                <Stack spacing={2}>
                                    <Box sx={{ borderBottom: 1, borderColor: 'divider', my: 2 }} />
                                    <Box sx={{ maxWidth: 520, mx: 'auto', width: '100%' }}>
                                        <Stack spacing={2}>
                                            <InfoPair label="DOB" value={formatDob(defendant.dob)} />
                                            <InfoPair label="Phone" value={formatPhoneNumber(defendant.phone)} />
                                            <InfoPair
                                                label="Address"
                                                value={[defendant.address, defendant.city, defendant.state, defendant.zip]
                                                    .filter(Boolean)
                                                    .join(', ')}
                                            />
                                            <InfoPair
                                                label="Last Check-in"
                                                value={
                                                    lastCheckin?.date
                                                        ? dayjs(lastCheckin.date).format('MMM D, YYYY h:mm A')
                                                        : '—'
                                                }
                                            />
                                        </Stack>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader title="Activity Log" subheader="Most recent activity from this defendant" />
                            <CardContent sx={{ px: 0 }}>
                                {activityItems.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 2 }}>
                                        No activity recorded yet.
                                    </Typography>
                                ) : (
                                    <TableContainer
                                        sx={(theme) => ({
                                            maxHeight: 560,
                                            overflowY: 'auto',
                                            overflowX: 'hidden',
                                            border: 0,
                                            boxShadow: 'none',
                                            backgroundColor: 'transparent',
                                            '&::-webkit-scrollbar-track': {
                                                backgroundColor: '#121212',
                                            },
                                        })}
                                    >
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell align="right">Location</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {activityItems.map((item) => (
                                                    <TableRow
                                                        key={item.id}
                                                        hover
                                                        onClick={() => {
                                                            if (item.type === 'checkin') {
                                                                openCheckinDialog(item.raw);
                                                            } else if (item.location) {
                                                                openMapForLocation(item.location);
                                                            }
                                                        }}
                                                        sx={{ cursor: item.type === 'checkin' || item.location ? 'pointer' : 'default' }}
                                                    >
                                                        <TableCell>
                                                            {dayjs(item.date).format('MMM D, YYYY h:mm A')}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                size="small"
                                                                label={item.status}
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {item.location ? (
                                                                <Button
                                                                    size="small"
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        openMapForLocation(item.location);
                                                                    }}
                                                                >
                                                                    View Map
                                                                </Button>
                                                            ) : (
                                                                '—'
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </CardContent>
                        </Card>

                    </Stack>
                </Grid>

                <Grid item xs={12} lg={4}>
                    <Stack spacing={3}>
                        <Card>
                            <CardHeader
                                title="Check-in Schedule"
                                action={
                                    <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<Edit />}
                                        onClick={() => setScheduleEditorOpen(true)}
                                        sx={{ color: 'common.white' }}
                                    >
                                        Edit
                                    </Button>
                                }
                            />
                            <CardContent>
                                {schedule ? (
                                    <Stack spacing={2}>
                                        <InfoPair label="Frequency" value={schedule.frequency} />
                                        <InfoPair
                                            label="Start"
                                            value={
                                                schedule.startDate
                                                    ? dayjs(schedule.startDate).format('MMM D, YYYY')
                                                    : '—'
                                            }
                                        />
                                        <InfoPair
                                            label="End"
                                            value={
                                                schedule.endDate
                                                    ? dayjs(schedule.endDate).format('MMM D, YYYY')
                                                    : '—'
                                            }
                                        />
                                        <InfoPair
                                            label="Total Check-ins"
                                            value={schedule.checkins?.length ? schedule.checkins.length : '—'}
                                        />
                                    </Stack>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No check-in schedule configured.
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>

                        <CheckinScheduleEditor
                            open={scheduleEditorOpen}
                            onClose={() => setScheduleEditorOpen(false)}
                            defendantId={defendant.id}
                            schedule={schedule}
                            onScheduleGenerated={handleScheduleGenerated}
                        />

                        <Card>
                            <CardHeader
                                title="Geofence Alerts"
                                action={
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={handleOpenGeofence}
                                        disabled={geofenceLoading}
                                        sx={{ color: 'common.white' }}
                                        startIcon={<Edit />}
                                    >
                                        {geofenceLoading ? 'Loading...' : 'Edit'}
                                    </Button>
                                }
                            />
                            <CardContent>
                                <Typography variant="body2" color="text.secondary">
                                    Geofences let you define safe zones for this defendant. Add a location to receive
                                    entry and exit alerts.
                                </Typography>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader
                                title="Last Known Location"
                            />
                            <CardContent>
                                {mapSrc ? (
                                    <>
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: 320,
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                mb: 2,
                                            }}
                                        >
                                            <iframe
                                                title="Last known location map"
                                                width="100%"
                                                height="100%"
                                                frameBorder="0"
                                                style={{ border: 0 }}
                                                src={mapSrc}
                                                allowFullScreen
                                            />
                                        </Box>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={4}>
                                                <InfoPair
                                                    label="Latitude"
                                                    value={
                                                        typeof lastKnownCoords.lat === 'number'
                                                            ? lastKnownCoords.lat.toFixed(5)
                                                            : lastKnownCoords.lat
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <InfoPair
                                                    label="Longitude"
                                                    value={
                                                        typeof lastKnownCoords.lng === 'number'
                                                            ? lastKnownCoords.lng.toFixed(5)
                                                            : lastKnownCoords.lng
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <InfoPair
                                                    label="Accuracy"
                                                    value={
                                                        lastKnownCoords.accuracy
                                                            ? `${lastKnownCoords.accuracy} m`
                                                            : '—'
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <InfoPair
                                                    label="Recorded"
                                                    value={
                                                        lastKnownTimestamp
                                                            ? dayjs(
                                                                  lastKnownTimestamp
                                                              ).format('MMM D, YYYY h:mm A')
                                                            : '—'
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No location data available yet. Ping the device to request an updated location.
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Stack>
                </Grid>
            </Grid>

            <MapDialog open={mapOpen} location={mapLocation} onClose={() => setMapOpen(false)} />
            <Dialog
                open={checkinDialogOpen}
                onClose={closeCheckinDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Check-in Summary</DialogTitle>
                <DialogContent>
                    {selectedCheckin ? (
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'flex-start',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Box sx={{ minWidth: 120 }}>
                                    {selectedCheckin.photoUrl ? (
                                        <CheckInPhoto
                                            src={selectedCheckin.photoUrl}
                                            alt="Check-in photo"
                                            variant="rounded"
                                            sx={{ width: 110, height: '100%', borderRadius: 2 }}
                                        />
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            No photo uploaded.
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Stack spacing={1}>
                                        <InfoPair
                                            label="Date"
                                            value={dayjs(selectedCheckin.date).format('MMM D, YYYY h:mm A')}
                                        />
                                        <InfoPair label="Status" value={selectedCheckin.status || 'Pending'} />
                                        <InfoPair
                                            label="Location"
                                            value={
                                                selectedCheckin.location?.coords
                                                    ? `${selectedCheckin.location.coords.latitude}, ${selectedCheckin.location.coords.longitude}`
                                                    : selectedCheckin.location?.latitude &&
                                                      selectedCheckin.location?.longitude
                                                      ? `${selectedCheckin.location.latitude}, ${selectedCheckin.location.longitude}`
                                                      : '—'
                                            }
                                        />
                                    </Stack>
                                </Box>
                            </Box>
                            {selectedCheckin.location ? (
                                <Box
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        height: 300,
                                    }}
                                >
                                    <iframe
                                        title="Check-in location map"
                                        width="100%"
                                        height="100%"
                                        style={{ border: 0 }}
                                        loading="lazy"
                                        src={`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${
                                            selectedCheckin.location?.coords
                                                ? `${selectedCheckin.location.coords.latitude},${selectedCheckin.location.coords.longitude}`
                                                : selectedCheckin.location?.latitude &&
                                                  selectedCheckin.location?.longitude
                                                  ? `${selectedCheckin.location.latitude},${selectedCheckin.location.longitude}`
                                                  : ''
                                        }&zoom=15`}
                                    />
                                </Box>
                            ) : null}
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeCheckinDialog}>Close</Button>
                </DialogActions>
            </Dialog>
            <DefendantDialog
                open={editOpen}
                onClose={() => setEditOpen(false)}
                defendant={defendant}
                onSaved={handleDefendantSaved}
            />

            <Dialog
                open={geofenceOpen}
                onClose={handleCloseGeofence}
                maxWidth="sm"
                fullWidth
                TransitionProps={{
                    onEntered: () => setGeofenceDialogReady(true),
                    onExited: () => setGeofenceDialogReady(false),
                }}
            >
                <DialogTitle>{geofenceActionLabel}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Address"
                            name="address"
                            value={geofenceForm.address}
                            onChange={handleGeofenceChange}
                            fullWidth
                            helperText={
                                geofenceGeocoding
                                    ? 'Geocoding address...'
                                    : defendantAddress
                                      ? 'Prefilled from the defendant profile. You can edit it.'
                                      : 'Enter the address for this geofence.'
                            }
                        />
                        <TextField
                            label="Radius (miles)"
                            name="radiusMiles"
                            value={geofenceForm.radiusMiles}
                            onChange={handleGeofenceChange}
                            type="number"
                            fullWidth
                            inputProps={{ step: '0.01', min: '0' }}
                            disabled={geofenceForm.shapeType !== 'circle'}
                            helperText={
                                geofenceForm.shapeType === 'circle'
                                    ? 'Drag the circle or edit the miles to resize.'
                                    : 'Radius is only used for circles.'
                            }
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={geofenceAdvanced}
                                    onChange={(event) => {
                                        const next = event.target.checked;
                                        setGeofenceAdvanced(next);
                                        if (next) {
                                            setGeofenceDrawMode((prev) => prev || 'circle');
                                        }
                                    }}
                                />
                            }
                            label="Advanced edit (draw on map)"
                        />
                        {geofenceAdvanced ? (
                            <>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Button
                                        variant={geofenceDrawMode === 'pan' ? 'contained' : 'outlined'}
                                        size="small"
                                        onClick={() => handleGeofenceDrawModeChange(null, 'pan')}
                                        sx={{
                                            color:
                                                geofenceDrawMode === 'pan' ? 'common.white' : 'primary.main',
                                            borderColor:
                                                geofenceDrawMode === 'pan' ? undefined : 'primary.main',
                                            '&:hover': geofenceDrawMode === 'pan'
                                                ? undefined
                                                : { borderColor: 'primary.main' },
                                        }}
                                    >
                                        <PanTool fontSize="small" sx={{ mr: 1 }} />
                                        Pan
                                    </Button>
                                    <Button
                                        variant={geofenceDrawMode === 'circle' ? 'contained' : 'outlined'}
                                        size="small"
                                        onClick={() => handleGeofenceDrawModeChange(null, 'circle')}
                                        sx={{
                                            color:
                                                geofenceDrawMode === 'circle' ? 'common.white' : 'primary.main',
                                            borderColor:
                                                geofenceDrawMode === 'circle' ? undefined : 'primary.main',
                                            '&:hover': geofenceDrawMode === 'circle'
                                                ? undefined
                                                : { borderColor: 'primary.main' },
                                        }}
                                    >
                                        <RadioButtonUnchecked fontSize="small" sx={{ mr: 1 }} />
                                        Circle
                                    </Button>
                                    <Button
                                        variant={geofenceDrawMode === 'polygon' ? 'contained' : 'outlined'}
                                        size="small"
                                        onClick={() => handleGeofenceDrawModeChange(null, 'polygon')}
                                        sx={{
                                            color:
                                                geofenceDrawMode === 'polygon' ? 'common.white' : 'primary.main',
                                            borderColor:
                                                geofenceDrawMode === 'polygon' ? undefined : 'primary.main',
                                            '&:hover': geofenceDrawMode === 'polygon'
                                                ? undefined
                                                : { borderColor: 'primary.main' },
                                        }}
                                    >
                                        <Polyline fontSize="small" sx={{ mr: 1 }} />
                                        Polygon
                                    </Button>
                                    <Typography variant="caption" color="text.secondary">
                                        Choose a tool, then draw on the map.
                                    </Typography>
                                </Stack>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: 260,
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Box ref={handleGeofenceMapRef} sx={{ width: '100%', height: '100%' }} />
                                </Box>
                            </>
                        ) : null}
                        {geofenceError ? (
                            <Typography variant="body2" color="error">
                                {geofenceError}
                            </Typography>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCloseGeofence}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveGeofence}
                        disabled={geofenceSubmitting || geofenceGeocoding || geofenceLoading}
                        sx={{ color: 'common.white' }}
                    >
                        {geofenceSubmitting ? 'Saving...' : 'Save Geofence'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default DefendantProfile;
